import { useEffect, useState } from 'react';
import { getProgressSuspendableResource } from 'ContentProgress';
import { useAngularContext } from 'AngularContext';
import { type CurrentUser } from 'Users';
import { waitForPendingStreamSave } from './waitForPendingStreamSave';

/*
    On first render, progress comes from a cached copy that predates the lesson the student just finished.
    Wait for the pending save and a progress reload before the redirect guard runs.
*/
export function useCompletionSettled(currentUser: CurrentUser | null, streamId: string) {
    const $injector = useAngularContext();
    const [isSettled, setIsSettled] = useState(false);

    useEffect(() => {
        if (!currentUser) return undefined;

        let cancelled = false;

        /*
            Settle even when the save or the progress load fails. isSettled gates the redirect that
            sends a student off a course they have not finished, so leaving it false on an error
            strands them on the completed page.
        */
        const settle = async () => {
            try {
                await waitForPendingStreamSave($injector, streamId);
                await getProgressSuspendableResource(currentUser.id).load().promise;
            } finally {
                if (!cancelled) setIsSettled(true);
            }
        };

        settle().catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [currentUser, streamId, $injector]);

    return isSettled;
}
